import * as React from "react";
import { CPText, DateText } from "shared/components/base";
import { Chip } from "@progress/kendo-react-buttons";
import { CPGridV2, useCPGridV2 } from "shared/components/shared/CPGridV2";
import type {
  CPGridV2Column,
  CPGridV2FilterOption,
} from "shared/components/shared/CPGridV2";
import "./AdminAuditLogStyles.scss";
import { ValueObject } from "shared/types";
import { useQueryClient } from "react-query";
import {
  useGetAuditLogsForGridQuery,
  UseGetAuditLogsForGridQueryOptions,
} from "data/admin/admin";

const LEVEL_COLOR_MAP: Record<
  string,
  "warning" | "success" | "error" | "base" | "info"
> = {
  information: "success",
  warning: "warning",
  error: "error",
  fatal: "error",
  debug: "base",
  verbose: "base",
};

const COLUMNS: CPGridV2Column[] = [
  { field: "id", title: "ID", width: 80, hide: true },
  {
    field: "timeStamp",
    title: "Timestamp",
    width: 200,
    renderCell: (data: ValueObject) => (
      <DateText date={data.dataItem.timeStamp} format="MM/DD/YYYY hh:mm A" />
    ),
  },
  {
    field: "level",
    title: "Level",
    width: 130,
    renderCell: (data: ValueObject) => {
      const level: string = data.dataItem.level ?? "";
      const themeColor = LEVEL_COLOR_MAP[level.toLowerCase()] ?? "base";
      return <Chip text={level} themeColor={themeColor} size="small" />;
    },
  },
  {
    field: "eventType",
    title: "Event Type",
    width: 200,
    renderCell: (data: ValueObject) => (
      <CPText>{data.dataItem.eventType}</CPText>
    ),
  },
  {
    field: "userName",
    title: "User",
    width: 200,
    renderCell: (data: ValueObject) => (
      <CPText>{data.dataItem.userName}</CPText>
    ),
  },
  { field: "message", title: "Message" },
];

const FILTER_OPTIONS: CPGridV2FilterOption[] = [
  {
    filterType: "text",
    label: "Event Type",
    columnKey: "eventType",
    placeholder: "e.g. UserLogin",
  },
  {
    filterType: "dropdown",
    label: "Level",
    columnKey: "level",
    options: [
      { label: "Information", value: "Information" },
      { label: "Warning", value: "Warning" },
      { label: "Error", value: "Error" },
      { label: "Fatal", value: "Fatal" },
      { label: "Debug", value: "Debug" },
    ],
  },
  {
    filterType: "text",
    label: "User",
    columnKey: "userName",
    placeholder: "e.g. jsmith",
  },
  {
    filterType: "text",
    label: "Message",
    columnKey: "message",
    placeholder: "e.g. login failed",
  },
  {
    filterType: "date",
    label: "Date From",
    columnKey: "dateFrom",
  },
  {
    filterType: "date",
    label: "Date To",
    columnKey: "dateTo",
  },
];

const AdminAuditLogPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [filterParams, setFilterParams] =
    React.useState<UseGetAuditLogsForGridQueryOptions>({
      take: 20,
      skip: 0,
      ascending: false,
      sortBy: "TimeStamp",
    });

  const { data: logs, isFetching: isLogsLoading } =
    useGetAuditLogsForGridQuery(filterParams);

  const adapter = useCPGridV2({
    columns: COLUMNS,
    data: logs?.records ?? [],
    showLoader: isLogsLoading,
    totalRecords: logs?.totalRecords ?? 0,
  });

  return (
    <div className="admin-audit-log-page">
      <CPGridV2
        name="admin-audit-log-grid"
        title="Audit Log"
        adapter={adapter}
        compact
        sortable
        pageable
        noWrap
        gridLines="rows"
        filterOptions={FILTER_OPTIONS}
        onApplyFilters={(filters, paging) => {
          const { dateFrom, dateTo, ...rest } = filters as Record<
            string,
            unknown
          > & {
            dateFrom?: Date | null;
            dateTo?: Date | null;
          };
          setFilterParams({
            ...rest,
            ...paging,
            dateFrom: (dateFrom as Date | null)?.toISOString() ?? null,
            dateTo: (dateTo as Date | null)?.toISOString() ?? null,
          } as UseGetAuditLogsForGridQueryOptions);
        }}
        onClearFilters={() =>
          setFilterParams({
            take: 20,
            skip: 0,
            ascending: false,
            sortBy: "TimeStamp",
          })
        }
        onRefresh={() => queryClient.invalidateQueries("audit-logs-grid")}
      />
    </div>
  );
};

export default AdminAuditLogPage;
